import { CHNode } from '../../../../general/node';
import type { ValidationIssue } from '../contracts';

export type FallbackDictionary = Record<string, string>;
export type NodeFallback = {
  node: CHNode;
  fallback: FallbackDictionary;
  relpath: string;
  forcedMeta: FallbackDictionary;
  issues?: ValidationIssue[];
  pathSegments?: string[];
  excluded?: boolean;
  exclusionReason?: string;
};
export type NodeIDFallback = {
  nodeID: number;
  fallback: FallbackDictionary;
  relpath: string;
  forcedMeta: FallbackDictionary;
  issues?: ValidationIssue[];
  pathSegments?: string[];
  excluded?: boolean;
  exclusionReason?: string;
};
export type Fallback = FallbackDictionary | NodeFallback[] | NodeIDFallback[];

export const fallbackIsPlainDictionary = (f: Fallback): f is FallbackDictionary =>
  !Array.isArray(f);

export const fallbackIsNodeFallback = (f: Fallback): f is NodeFallback[] =>
  !fallbackIsPlainDictionary(f) && f.length > 0 && 'node' in f[0];
export const fallbackIsNodeIDFallback = (f: Fallback): f is NodeIDFallback[] =>
  !fallbackIsPlainDictionary(f) && f.length > 0 && 'nodeID' in f[0];

export const getFallbackForNode = (f: Fallback, node: CHNode | number): FallbackDictionary => {
  if (fallbackIsPlainDictionary(f)) return f;
  const id = typeof node === 'number' ? node : node.id;
  if (fallbackIsNodeIDFallback(f)) {
    return f.find((x) => x.nodeID === id)?.fallback ?? {};
  }
  if (fallbackIsNodeFallback(f)) {
    return f.find((x) => x.node.id === id)?.fallback ?? {};
  }
  return {};
};

export const getForcedMetaForNode = (f: Fallback, node: CHNode | number): FallbackDictionary => {
  if (fallbackIsPlainDictionary(f)) return {};
  const id = typeof node === 'number' ? node : node.id;
  if (fallbackIsNodeIDFallback(f)) return f.find((x) => x.nodeID === id)?.forcedMeta ?? {};
  if (fallbackIsNodeFallback(f)) return f.find((x) => x.node.id === id)?.forcedMeta ?? {};
  return {};
};
